import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Drawer, message } from "antd";

import { ROLE_OPTIONS } from "@/contexts/AssumedRoleContext";
import { useScopeParams } from "@/hooks/useScopeParams";
import { useDirectFileDownload } from "@/hooks/useDirectFileDownload";
import { documentWorkspaceService } from "@/services/documentWorkspaceService";
import { RoleAccessBadge } from "@/components/DocumentWorkspace/RoleAccessBadge";
import { DocumentStateBadge } from "@/components/DocumentWorkspace/DocumentStateBadge";
import { ALL_ROLES } from "@/views/DocumentWorkspace/documentWorkspaceData";
import { formatFileSize } from "@/views/DocumentWorkspace/documentWorkspaceMapping";
import type { StudyDocumentRow } from "@/views/DocumentWorkspace/documentWorkspace.types";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
    dateStyle: "medium",
  });
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function VersionHistoryDrawer({
  document,
  canEdit,
  canManageAccess,
  onClose,
  onUploadVersion,
  onEditAccess,
}: {
  document: StudyDocumentRow | null;
  /** True only for holders of the plan_editor role — the server enforces this
   * independently on the new-version endpoint; this only controls the affordance. */
  canEdit: boolean;
  /** True only for holders of `documents.manage-access`. */
  canManageAccess: boolean;
  onClose: () => void;
  onUploadVersion: (document: StudyDocumentRow) => void;
  onEditAccess: (document: StudyDocumentRow) => void;
}) {
  const { id: trialId } = useScopeParams();
  const { downloadFile, isDownloading } = useDirectFileDownload({
    onError: () => message.error("Couldn't open that version. Try again."),
  });

  return (
    <Drawer
      open={!!document}
      onClose={onClose}
      size={620}
      title={document?.title ?? ""}
      destroyOnHidden
      extra={
        canEdit && document && document.current_version_state !== "retired" ? (
          <Button
            type="primary"
            size="small"
            icon={<UploadOutlined />}
            onClick={() => onUploadVersion(document)}
          >
            New version
          </Button>
        ) : null
      }
    >
      {document ? (
        <div className="flex flex-col gap-4">
          {document.current_version_state === "retired" ? (
            <Alert
              type="warning"
              showIcon
              title="Retired from Ask Vivo context"
              description="This document no longer informs any answer, but it and its versions remain, because study records still reference them."
            />
          ) : null}

          <div className="rounded-[8px] border border-[#E5E7EB] bg-[#FAFBFC] p-3">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="mb-1 text-[12px] font-semibold uppercase tracking-wide text-[#6B7280]">
                  Role access
                </p>
                <RoleAccessBadge roleAccess={document.role_access} />
                <p className="mt-1.5 text-[12px] text-[#6B7280]">
                  {document.role_access.length >= ALL_ROLES.length
                    ? "Every role on the study can open this."
                    : document.role_access
                        .map(
                          (role) =>
                            ROLE_OPTIONS.find((option) => option.key === role)
                              ?.label ?? role,
                        )
                        .join(" · ")}
                </p>
              </div>

              {canManageAccess ? (
                <Button size="small" onClick={() => onEditAccess(document)}>
                  Change access
                </Button>
              ) : null}
            </div>
          </div>

          <div>
            <h4 className="mb-1 text-[14px] font-semibold text-[#1F2933]">
              Current version
            </h4>
            <div className="rounded-[8px] border border-[#E5E7EB] p-3 text-[13px]">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-[#1F2933]">
                  {document.current_version_amendment_label ??
                    `Version ${document.current_version}`}
                </span>
                <span className="font-mono text-[11px] text-[#8AA0A9]">
                  v{document.current_version}
                </span>
                <DocumentStateBadge state={document.current_version_state} />
              </div>

              <div className="mt-1 text-[12px] text-[#374151]">
                {document.rollout_scope === "study" ? (
                  <>
                    Effective{" "}
                    {formatDate(document.current_version_effective_from)}
                    {document.current_version_effective_to
                      ? ` → ${formatDate(document.current_version_effective_to)}`
                      : " → present"}
                  </>
                ) : (
                  <>
                    Per-site rollout
                    {document.current_version_approved_on
                      ? ` · sponsor approved ${formatDate(document.current_version_approved_on)}`
                      : ""}
                  </>
                )}
              </div>

              <div className="text-[12px] text-[#8AA0A9]">
                Uploaded by {document.uploaded_by_name} ·{" "}
                {formatTimestamp(document.uploaded_at)} ·{" "}
                {formatFileSize(document.current_version_file_size_bytes)}
              </div>

              <div className="mt-1 text-[12px]">
                {document.current_version_referencing_records > 0 ? (
                  <span className="text-[#06709B]">
                    {document.current_version_referencing_records.toLocaleString()}{" "}
                    records reference this version
                  </span>
                ) : (
                  <span className="text-[#8AA0A9]">
                    No records reference it yet
                  </span>
                )}
                <Button
                  size="small"
                  type="link"
                  icon={<DownloadOutlined />}
                  className="ml-2 px-0"
                  loading={isDownloading}
                  onClick={() =>
                    downloadFile({
                      fileUrl: documentWorkspaceService.buildVersionContentUrl(
                        trialId,
                        document.id,
                        document.current_version_id,
                      ),
                      fileName: document.current_version_original_filename,
                    })
                  }
                >
                  Open
                </Button>
              </div>

              {document.current_version_note ? (
                <div className="mt-1 italic text-[#374151]">
                  “{document.current_version_note}”
                </div>
              ) : null}
            </div>
            {/* Only the current version's fields come back from the dataset row today —
                a full amendment lineage needs a dataset over StudyDocumentVersion. */}
          </div>

          <div>
            <h4 className="mb-1 text-[14px] font-semibold text-[#1F2933]">
              Facts
            </h4>
            <div className="flex flex-col gap-1 text-[12px] text-[#374151]">
              <div>Created {formatTimestamp(document.created_at)}</div>
              <div>
                Last modified {formatTimestamp(document.last_modified_at)}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}
